import React, { FunctionComponent, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import { css } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { truncateEmail } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/stringUtils'
import { isValidEmailString } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/validationUtils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { ETHNICITY, GENDER_IDENTITY } from '@lyrahealth-inc/shared-app-logic/src/models/registration/Registration'

import { BackButtonV2 } from '../../atoms/backButton/BackButtonV2'
import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { CopyToClipboard } from '../../atoms/copyToClipboard/CopyToClipboard'
import { CheckIcon } from '../../atoms/icons/CheckIcon'
import { EventsIcon } from '../../atoms/icons/EventsIcon'
import { MenuDotsIcon } from '../../atoms/icons/MenuDotsIcon'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { IS_WEB } from '../../constants'
import { DotItem, DotSeparatedRow } from '../../molecules/dotSeparatedRow/DotSeparatedRow'
import { ProfileCardBadge, SPECIAL_ALERT_BADGE_TEXT } from '../../molecules/profileCardBadge/ProfileCardBadge'
import { Details } from '../../ui-models/messages/Details'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import {
  ClientDetailsTabNavigation,
  ClientDetailsTabType,
} from '../clientDetailsTabNavigation/ClientDetailsTabNavigation'
import { Menu, MenuItemProps } from '../menu/Menu'
import { MessagingHub } from '../messagingHub/MessagingHub'

const Container = styled.View<{ theme: ThemeType; isCollapsed?: boolean; stickyTopOffset?: number }>(
  ({ theme: { colors, spacing }, isCollapsed, stickyTopOffset = 0 }) => ({
    backgroundColor: colors.backgroundElement,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderDefault,
    borderStyle: 'solid',
    paddingTop: isCollapsed ? spacing['16px'] : spacing['40px'],
    paddingHorizontal: spacing['16px'],
    gap: isCollapsed ? spacing['8px'] : spacing['24px'],
    zIndex: 10,
    ...(IS_WEB && {
      position: 'sticky',
      top: stickyTopOffset,
      transition: 'padding-top 150ms ease, box-shadow 150ms ease',
    }),
    ...(IS_WEB && isCollapsed && { boxShadow: `0 2px 10px ${colors.shadowLow}` }),
  }),
)

const ItemsContainer = styled.View<{ theme: ThemeType }>(() => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  zIndex: 2,
}))

const TopRow = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  paddingLeft: spacing['16px'],
  flex: 1,
}))

const BadgeRow = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  marginBottom: spacing['4px'],
  gap: spacing['8px'],
}))

const NameAndDetails = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flex: 1,
  gap: spacing['4px'],
}))

const ActionsContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['16px'],
}))

const PhoneNumberContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['4px'],
}))

const EmailContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
  flexShrink: 1,
}))

const CopyIdButtonContainer = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
}))

const TabNavigationContainer = styled.View<{ theme: ThemeType; hiddenBackButton?: boolean }>(
  ({ theme: { spacing }, hiddenBackButton }) => ({
    flexDirection: 'row',
    paddingLeft: hiddenBackButton ? spacing['16px'] : spacing['48px'],
    zIndex: 1,
  }),
)

// Shared styling for the header action buttons (calendar, messages, menu)
const HEADER_ICON_BUTTON_SIZE = 48
const headerActionButtonStyle = (backgroundColor: string): ViewStyle => ({
  height: HEADER_ICON_BUTTON_SIZE,
  paddingVertical: 8,
  paddingHorizontal: 12,
  borderColor: 'transparent',
  backgroundColor,
})

const headerIconButtonStyle = (backgroundColor: string): ViewStyle => ({
  width: HEADER_ICON_BUTTON_SIZE,
  height: HEADER_ICON_BUTTON_SIZE,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 100,
  borderWidth: 1.5,
  borderColor: 'transparent',
  backgroundColor,
})

export type ClientProfileHeaderBarProps = {
  clientDetails: Details | null
  tabs: ClientDetailsTabType[]
  dropdownOptions: MenuItemProps[]
  onBackPress?: () => void
  messagingPopover: React.FC<{ closePopover: () => void; className?: string }>
  forceMessagingHubToOpenPopover: boolean
  onMessagesPopoverOpen: () => void
  numUnreadMessages: number
  selectedEpisodeId?: string
  showEmail?: boolean
  riskBadgeSlot?: React.ReactNode
  caregiverSubtitle?: React.ReactNode
  onSchedulePress?: () => void
  hideScheduleSessionButton?: boolean
  stickyTopOffset?: number
  containerRef?: (node: View | null) => void
  isCollapsed?: boolean
}

export const ClientProfileHeaderBar: FunctionComponent<ClientProfileHeaderBarProps> = ({
  clientDetails,
  tabs,
  dropdownOptions,
  onBackPress,
  messagingPopover,
  forceMessagingHubToOpenPopover,
  onMessagesPopoverOpen,
  numUnreadMessages,
  selectedEpisodeId,
  showEmail = false,
  riskBadgeSlot,
  caregiverSubtitle,
  onSchedulePress = () => {},
  hideScheduleSessionButton = false,
  stickyTopOffset = 0,
  containerRef,
  isCollapsed = false,
}) => {
  const { colors, spacing } = useTheme() as ThemeType
  const intl = useIntl()
  const { formatMessage } = intl

  const client = clientDetails?.client
  const programName = clientDetails?.episode.programName

  const detailItems = useMemo<DotItem[]>(() => {
    if (!client) {
      return []
    }
    const {
      legalName: clientLegalName,
      country: clientCountry,
      firstName: clientFirstName,
      age,
      phoneNumber,
      canLeaveVoicemail,
      dob,
      gender,
      ethnicity,
      timeZone,
      lyraId,
      pronouns,
      email,
      hideClientContactInfo,
      hideClientPhone,
    } = client

    const nameItem = clientLegalName ? (
      <BodyText
        key='legalName'
        text={
          <FormattedMessage
            defaultMessage='{clientName} (legal)'
            description="Label for client's legal name"
            values={{ clientName: clientLegalName }}
          />
        }
        size={BodyTextSize.SMALL}
        testID={tID('ClientProfileHeaderBar-legalName')}
      />
    ) : undefined

    const genderText =
      gender?.toLowerCase() === GENDER_IDENTITY.PREFER_NOT_TO_SAY
        ? formatMessage({
            defaultMessage: 'Gender not shared',
            description: 'Shown in the client details header when the client declined to share their gender',
          })
        : gender
    const genderPronouns = genderText && pronouns ? `${genderText} (${pronouns})` : genderText || pronouns
    const genderItem = genderPronouns ? (
      <BodyText
        key='genderPronouns'
        text={genderPronouns}
        size={BodyTextSize.SMALL}
        testID={tID('ClientProfileHeaderBar-genderPronouns')}
      />
    ) : undefined

    const ethnicityText =
      ethnicity === ETHNICITY.PREFER_NOT_TO_SAY
        ? formatMessage({
            defaultMessage: 'Ethnicity not shared',
            description: 'Shown in the client details header when the client declined to share their ethnicity',
          })
        : ethnicity
    const ethnicityItem = ethnicityText ? (
      <BodyText
        key='ethnicity'
        text={ethnicityText}
        size={BodyTextSize.SMALL}
        testID={tID('ClientProfileHeaderBar-ethnicity')}
      />
    ) : undefined

    const ageDob = age
      ? formatMessage(
          {
            defaultMessage: 'Age {age}{hasDob, select, true { ({dob})} other {}}',
            description: "Label pairing the client's age with their date of birth",
          },
          { age, dob, hasDob: Boolean(dob) },
        )
      : dob
    const ageItem = ageDob ? (
      <BodyText key='age' text={ageDob} size={BodyTextSize.SMALL} testID={tID('ClientProfileHeaderBar-age')} />
    ) : undefined

    const locationTimezone = clientCountry && timeZone ? `${clientCountry} (${timeZone})` : clientCountry || timeZone
    const locationItem = locationTimezone ? (
      <BodyText
        key='location'
        text={locationTimezone}
        size={BodyTextSize.SMALL}
        testID={tID('ClientProfileHeaderBar-location')}
      />
    ) : undefined

    const phoneItem = phoneNumber ? (
      <PhoneNumberContainer key='phone'>
        <BodyText text={phoneNumber} size={BodyTextSize.SMALL} testID={tID('ClientProfileHeaderBar-phoneNumber')} />
        {canLeaveVoicemail && (
          <View testID={tID('Voicemail-checkmark')}>
            <Tooltip
              placement='top'
              content={
                <FormattedMessage
                  defaultMessage='{clientFirstName} provided voicemail consent'
                  description='Tooltip message indicating that the client has provided voicemail consent'
                  values={{ clientFirstName }}
                />
              }
            >
              <CheckIcon fillColor={colors.iconSuccess} size={24} />
            </Tooltip>
          </View>
        )}
      </PhoneNumberContainer>
    ) : undefined

    const emailItem =
      showEmail && email && isValidEmailString(email) ? (
        <EmailContainer key='email'>
          <Tooltip content={email} placement='bottom' hoverEnabled>
            <BodyText
              text={truncateEmail(email)}
              size={BodyTextSize.SMALL}
              testID={tID('ClientProfileHeaderBar-email')}
              numberOfLines={1}
            />
          </Tooltip>
          <CopyToClipboard
            copyText={email}
            fillColor={colors.iconActive}
            size={18}
            testID={tID('ClientProfileHeaderBar-email-copy')}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Copy email address',
              description: 'Accessibility label for the button that copies client email to the clipboard',
            })}
          />
        </EmailContainer>
      ) : undefined

    const copyIdItem = lyraId ? (
      <CopyIdButtonContainer key='lyraId'>
        <CopyToClipboard
          displayTextSize={BodyTextSize.SMALL}
          displayText={
            <FormattedMessage
              defaultMessage='Copy ID'
              description='Label for the button that copies client`s Lyra ID to the clipboard'
            />
          }
          accessibilityLabel={formatMessage({
            defaultMessage: 'Copy ID',
            description: 'Accessibility label for the button that copies client`s Lyra ID to the clipboard',
          })}
          copyText={lyraId}
          testID={tID('ClientProfileHeaderBar-lyraId')}
          containerCustomStyle={css`
            flex-direction: row-reverse;
            justify-content: center;
            align-items: center;
            gap: 8px;
            text-decoration-line: underline;
            text-decoration-color: ${colors.textActive};
          `}
        />
      </CopyIdButtonContainer>
    ) : undefined

    return [
      nameItem,
      genderItem,
      ethnicityItem,
      ageItem,
      locationItem,
      hideClientContactInfo || hideClientPhone ? undefined : phoneItem,
      hideClientContactInfo ? undefined : emailItem,
      copyIdItem,
    ].filter(Boolean)
  }, [client, showEmail, colors.iconSuccess, colors.iconActive, colors.textActive, formatMessage])

  if (!client) {
    return null
  }

  return (
    <Container
      ref={containerRef}
      testID={tID('ClientProfileHeaderBar')}
      isCollapsed={isCollapsed}
      stickyTopOffset={stickyTopOffset}
    >
      <ItemsContainer>
        {onBackPress && <BackButtonV2 onPress={onBackPress} style={{ padding: spacing['8px'] }} />}
        <TopRow>
          <NameAndDetails>
            {!isCollapsed && (
              <BadgeRow>
                {client.showSpecialAlertBadge && (
                  <ProfileCardBadge text={SPECIAL_ALERT_BADGE_TEXT} specialAlertType={client.specialAlertType} />
                )}
                {riskBadgeSlot}
                {programName && <ProfileCardBadge text={getProgramDisplayLabel(intl, programName, 'clientProfile')} />}
              </BadgeRow>
            )}
            <Title text={client.preferredName || client.legalName || client.firstName} size={TitleSize.LARGE} />
            {!isCollapsed && caregiverSubtitle && (
              <View testID={tID('ClientProfileHeaderBar-caregiverSubtitle')}>
                {typeof caregiverSubtitle === 'string' ? (
                  <BodyText text={caregiverSubtitle} size={BodyTextSize.DEFAULT} color={colors.textSecondary} />
                ) : (
                  caregiverSubtitle
                )}
              </View>
            )}
            {!isCollapsed && <DotSeparatedRow items={detailItems} />}
          </NameAndDetails>
          <ActionsContainer>
            {!hideScheduleSessionButton && (
              <SecondaryButton
                testID={tID('ClientProfileHeaderBar-calendar-button')}
                accessibilityLabel={formatMessage({
                  defaultMessage: 'Schedule session',
                  description: 'Accessibility label for the schedule session button',
                })}
                onPress={onSchedulePress}
                text={
                  <FormattedMessage
                    defaultMessage='Schedule session'
                    description='Label for the schedule session button'
                  />
                }
                leftIcon={<EventsIcon size={24} />}
                iconColor={colors.iconDefault}
                customTextColor={colors.textPrimary}
                customTextSize={BodyTextSize.SMALL}
                style={headerActionButtonStyle(colors.backgroundPrimary)}
              />
            )}
            <MessagingHub
              messagingPopover={messagingPopover}
              forceMessagingHubToOpenPopover={forceMessagingHubToOpenPopover}
              onMessagesPopoverOpen={onMessagesPopoverOpen}
              numUnreadMessages={numUnreadMessages}
              selectedEpisodeId={selectedEpisodeId}
              triggerStyle={headerActionButtonStyle(colors.backgroundPrimary)}
              triggerText={
                <FormattedMessage defaultMessage='Message' description='Label for the message client button' />
              }
              triggerTextColor={colors.textPrimary}
              triggerTextSize={BodyTextSize.SMALL}
              highlightIconWhenOpen
            />
            <Menu
              testID={tID('ClientProfileHeaderBar-actions-menu')}
              customMenuIcon={(isOpen) => (
                <MenuDotsIcon vertical fillColor={isOpen ? colors.iconActive : colors.iconDefault} />
              )}
              tooltipContent={
                <FormattedMessage
                  defaultMessage='More'
                  description='Tooltip shown when hovering over the actions menu button'
                />
              }
              tooltipOffset={4}
              closeTooltipOnScroll
              pressableTriggerStyle={() => headerIconButtonStyle(colors.backgroundPrimary)}
              dropdownWidth={321}
              menuItems={dropdownOptions}
              horizontalPosition='right'
              menuItemStyle={{
                paddingLeft: spacing['24px'],
                paddingRight: spacing['24px'],
              }}
              dropdownStyle={{
                top: '114%',
              }}
            />
          </ActionsContainer>
        </TopRow>
      </ItemsContainer>
      <TabNavigationContainer hiddenBackButton={!onBackPress}>
        <ClientDetailsTabNavigation backgroundColor={colors.backgroundElement} tabs={tabs} horizontalClientDetails />
      </TabNavigationContainer>
    </Container>
  )
}
